import { useFileUploadMutation } from 'lib-knowledge-management-common/graphql';
import { extractFilename } from 'lib-knowledge-utils/string';
import { useCallback, useState } from 'react';

export type ImageProperties = {
  url: string;
  filename: string;
  width: number | undefined;
  height: number | undefined;
};

export type UploadFn = (
  fileToUpload: File,
) => Promise<ImageProperties | undefined>;
type Loading = boolean;

const maxSize = 50 * 1024 * 1024;
const maxAnimatedGifSize = 6 * 1024 * 1024;

/**
 * Looks for the "delay" bytes in the gif file. These bytes will be set to non 0
 * value if the gif file is animated. See for details -
 * https://giflib.sourceforge.net/whatsinagif/bits_and_bytes.html And -
 * http://www.matthewflickinger.com/lab/whatsinagif/bits_and_bytes.asp
 */
function isAnimatedGif(buffer: ArrayBuffer) {
  const HEADER_LEN = 6; // offset bytes for the header section
  const LOGICAL_SCREEN_DESC_LEN = 7; // offset bytes for logical screen description section

  // Start from last 4 bytes of the Logical Screen Descriptor
  const dv = new DataView(buffer, HEADER_LEN + LOGICAL_SCREEN_DESC_LEN - 3);
  let offset = 0;
  const globalColorTable = dv.getUint8(0); // aka packet byte
  let globalColorTableSize = 0;

  // Check first bit, if 0, then we don't have a Global Color Table
  if (globalColorTable & 0x80) {
    // Take the last 3 bits, to calculate the global color table size -> RGB * 2^(N+1)
    // N is the value in the last 3 bits.
    globalColorTableSize = 3 * 2 ** ((globalColorTable & 0x7) + 1);
  }

  // Move on to the Graphics Control Extension
  offset = 3 + globalColorTableSize;

  const extensionIntroducer = dv.getUint8(offset);
  const graphicsConrolLabel = dv.getUint8(offset + 1);
  let delayTime = 0;

  // Graphics Control Extension section is where gif animation data is stored
  // First 2 bytes must be 0x21 and 0xF9
  if (extensionIntroducer & 0x21 && graphicsConrolLabel & 0xf9) {
    // Skip to the 2 bytes with the delay time
    delayTime = dv.getUint16(offset + 4);
  }

  return delayTime > 0;
}

async function validateImageSize(fileToUpload: File): Promise<Error | null> {
  if (fileToUpload.type === 'image/gif') {
    const buffer = await fileToUpload.arrayBuffer();
    try {
      if (isAnimatedGif(buffer) && fileToUpload.size > maxAnimatedGifSize) {
        return new Error('File size exceeds max allowed');
      }
    } catch {}
  }

  if (fileToUpload.size > maxSize) {
    return new Error('File size exceeds max allowed');
  }

  return null;
}

export function useUploadImage(): [UploadFn, Error | undefined, Loading] {
  const [mutate, { error, reset }] = useFileUploadMutation();
  const [uploadError, setUploadError] = useState<Error | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const uploadImage = useCallback(
    async (fileToUpload: File) => {
      reset();
      setUploadError(undefined);

      const imageSizeError = await validateImageSize(fileToUpload);
      if (imageSizeError) {
        setUploadError(imageSizeError);
        return;
      }

      // Get the upload url
      const { data } = await mutate({
        variables: { fileName: fileToUpload.name },
      });
      const fileUploadRequest = data?.fileUploadRequest;
      if (!fileUploadRequest) {
        setUploadError(new Error("Upload URL can't be found"));
        return;
      }

      const imageProperties: ImageProperties = {
        url: fileUploadRequest.availableAt,
        filename: extractFilename(fileUploadRequest.availableAt),
        width: undefined,
        height: undefined,
      };

      // Generate Headers
      const headers: { [k: string]: string } = {
        'Content-Type': fileUploadRequest.contentType,
      };
      fileUploadRequest.customHeaders.forEach((h) => {
        const [k, v] = h.split(':');
        headers[k] = v;
      });

      // Upload the file
      try {
        setLoading(true);
        await fetch(fileUploadRequest.uploadUrl, {
          method: fileUploadRequest.method,
          headers,
          body: fileToUpload,
        });
      } catch (e) {
        setLoading(false);
        setUploadError(e instanceof Error ? e : new Error('Upload failed'));
        return;
      }

      // Get image attributes stored in image headers.
      try {
        setLoading(true);
        // The GET request is cached by the browser, so loading here isn't wasted
        const res = await fetch(
          imageURLWithEditorWidth(fileUploadRequest.availableAt),
          {
            method: 'GET',
          },
        );

        if (!res.ok) {
          setLoading(false);
          setUploadError(
            new Error(
              res.status === 413
                ? 'File size exceeds max allowed'
                : 'Upload failed',
            ),
          );

          return;
        }

        imageProperties.width =
          Number.parseInt(res.headers.get('x-amz-meta-original-width') || '') ||
          undefined;
        imageProperties.height =
          Number.parseInt(
            res.headers.get('x-amz-meta-original-height') || '',
          ) || undefined;
      } catch (e) {
        setUploadError(e instanceof Error ? e : new Error('Upload failed'));
        return;
      } finally {
        setLoading(false);
      }

      return imageProperties;
    },
    [mutate, reset],
  );

  return [uploadImage, error || uploadError, loading];
}

const maxEditorWidth = 1280;

/**
 * Adds the width param to the image url, so the editor is served an image sharp
 * enough for the screen showing it.
 *
 * Capped at the width the image was uploaded at, because asking for more than
 * the source holds returns an enlarged copy: larger, and no sharper.
 */
export function imageURLWithEditorWidth(
  url: string,
  originalWidth?: number,
): string {
  return `${url}?width=${Math.min(maxEditorWidth, originalWidth || maxEditorWidth)}`;
}
